import { Capacitor } from "@capacitor/core";

/**
 * Attribution fields captured on first app open. Saved to the user doc when
 * a new account is created so we can see where our traffic comes from in the
 * admin panel. Every field is optional — the exact mix depends on how the
 * user arrived (ad click, share link, organic install, etc.).
 */
export interface InstallSource {
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmContent?: string;
  utmTerm?: string;
  /** Where the user came from (document.referrer). Empty for direct visits. */
  referrer?: string;
  /** "web" | "android" | "ios" — useful for separating install paths. */
  platform?: string;
  /** First URL the user landed on. Useful for deep-link attribution. */
  landingPath?: string;
  /** When attribution was first captured (ISO string). */
  capturedAt?: string;
}

const STORAGE_KEY = "onyxlife_install_source";

function readStored(): InstallSource | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as InstallSource;
  } catch {
    return null;
  }
}

/**
 * Captures attribution from the current page URL + referrer on first load
 * and persists it to localStorage so it survives navigation. Idempotent:
 * subsequent calls return the previously-captured value unchanged.
 *
 * Call once on app bootstrap (App.tsx) so the data is ready by the time a
 * user doc is created.
 */
export function captureInstallSource(): InstallSource {
  const existing = readStored();
  if (existing) return existing;

  const params = new URLSearchParams(window.location.search);
  const captured: InstallSource = {
    utmSource: params.get("utm_source") || undefined,
    utmMedium: params.get("utm_medium") || undefined,
    utmCampaign: params.get("utm_campaign") || undefined,
    utmContent: params.get("utm_content") || undefined,
    utmTerm: params.get("utm_term") || undefined,
    referrer: document.referrer || undefined,
    platform: Capacitor.getPlatform(),
    landingPath: window.location.pathname + window.location.search,
    capturedAt: new Date().toISOString(),
  };

  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(captured));
  } catch {
    // ignore storage errors — attribution is best-effort
  }
  return captured;
}

/**
 * Returns the previously-captured attribution, or captures it now if this
 * is the first call. Shaped as a plain object ready to spread into an
 * addDoc() payload — fields with no value are omitted.
 */
export function getInstallSourceForUserDoc(): Record<string, string> {
  const src = readStored() || captureInstallSource();
  const out: Record<string, string> = {};
  (Object.keys(src) as (keyof InstallSource)[]).forEach((key) => {
    const value = src[key];
    if (value) out[key] = value;
  });
  return out;
}
